import { useRouter } from 'next/router';
import React, { useState } from 'react';
import NonBenefitPharmacyHeader from 'src/features/pharmacies/pharmacies-nonbenefit/ui/NonBenefitPharmacyHeader';
import NonBenefitPharmacyHeaderFilter from 'src/features/pharmacies/pharmacies-nonbenefit/ui/NonBenefitPharmacyHeaderFilter';
import styled from 'styled-components';

import CustomBreadCrumbs from '@/components/atom/CustomBreadCrumbs';
import ClickLogger from '@/components/common/ClickLogger';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import LoginModal from '@/components/molecules/login-modal';
import ShareSelectModal, { KAKAO_SHARE_TYPE } from '@/components/molecules/modal/select-modal/share-select-modal';
import Overlay from '@/components/molecules/overlay';
import { BreadCrumbsWrapper } from '@/components/templates/communication/faq/faq-tag-list-template';
import { FindPharmacyLog } from '@/constants/logs/pharmacy';
import EmptyPharmacyToast from '@/features/pharmacies/shared/ui/empty-pharmacy-toast/EmptyPharmacyToast';
import PharmacyCardBottomSheet from '@/features/pharmacies/shared/ui/pharmacy-card-bottom-sheet/PharmacyCardBottomSheet';

import PharmacyMapContainer from '@/features/pharmacies/shared/ui/pharmacy-map-container/PharmacyMapContainer';

import useNonBenefitMapViewModel from '@/hooks/non-benefit/useNonBenefitMapViewModel';
import theme from '@/styles/theme';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { ENearbyPharmacySearchSortType } from '@/types/pharmacy';
import { formatShareUrl } from '@/utils/format';
import { getPageName } from '@/utils/logger';

import HealthMedicalMedicines from '@/components/atom/icon/layout/HealthMedicalMedicines';
import PharmacyListBottomSheet from '@/features/pharmacies/shared/ui/pharmacy-list-bottom-sheet/PharmacyListBottomSheet';
import PharmacyMapHeaderTabs from '@/features/pharmacies/shared/ui/pharmacy-map-header-tabs/PharmacyMapHeaderTabs';
import PharmacyInfoHeader from '../../pharmacy/detail/components/pharmacy-info-header';

interface Props extends CustomPageProps {
  viewModel: ReturnType<typeof useNonBenefitMapViewModel>;
  title: string;
}

const GLOBAL_HEADER_HEIGHT = 60;

const NonBenefitMapTemplate = ({ viewModel, client, title }: Props) => {
  const router = useRouter();
  const [isShowShareModal, setShowShareModal] = useState(false);

  const diagnosisTypeId = viewModel.selectedTab?.diagnosisTypeId;
  const treatmentProductMedicineName = viewModel.selectedMedicine?.treatmentProductMedicineName;
  const treatmentProductShareImageUrl = viewModel.selectedMedicine?.treatmentProductShareImageUrl;
  const treatmentMedicineId = viewModel.selectedTreatmentMedicine?.treatmentMedicineId;
  const treatmentMedicineUnitId = viewModel.selectedTreatmentMedicineUnit?.treatmentMedicineUnitId;

  const handleOpenShareModal = () => {
    setShowShareModal(true);
  };
  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  return (
    <TemplateLayout.Wrapper>
      {client.clientType === ClientType.WEB && (
        <BreadCrumbsWrapper>
          <CustomBreadCrumbs visible breadCrumbs={viewModel.breadCrumbs} />
        </BreadCrumbsWrapper>
      )}

      <TemplateLayout.Content>
        <ContentWrapper>
          <NonBenefitPharmacyHeader
            isMobile={client.isMobile}
            title={title}
            handleOpenShareModal={handleOpenShareModal}
          />
          <PharmacyMapHeaderTabs
            tabs={viewModel.tabs}
            selectedTab={viewModel.selectedTab}
            onClickTab={viewModel.onClickTab}
          />
          <PharmacyMapContainer
            selectedPharmacy={viewModel.selectedPharmacy}
            updatePharmacyMapInitiateError={viewModel.updatePharmacyMapInitiateError}
            level={6}
            isPharmaciesLoading={viewModel.isNearbyPharmaciesLoading}
            centerCoordinate={viewModel.currentLocation}
            pharmacies={viewModel.pharmacies}
            updateDongLiName={viewModel.updateDongLiName}
            updateMapCoordinate={viewModel.updateCurrentMapCoordinate}
            isVisibleMedicinePrice={viewModel.isVisibleMedicinePrice}
            shouldShowDiagnosisPrice={viewModel.isIncludeDiagnosisPriceFilterSelected}
            resetSelectedPharmacy={viewModel.resetSelectedPharmacy}
            updateSelectedPharmacy={viewModel.updateSelectedPharmacy}
            mapRef={viewModel.mapRef}
            headerHeight={(client.isMobile ? 156 : 152) - GLOBAL_HEADER_HEIGHT}
          />
        </ContentWrapper>

        <PharmacyListBottomSheet
          title={
            <NonBenefitPharmacyHeaderFilter
              client={client}
              selectedTab={viewModel.selectedTab}
              defaultSort={ENearbyPharmacySearchSortType.LowPrice}
              currentDongLi={viewModel.dongLiName}
              onApplyFilter={(filterCriteria) => viewModel.applyFilter(filterCriteria)}
              onClickCurrentLocation={viewModel.onClickCurrentLocation}
            />
          }
          client={client}
          ref={viewModel.pharmacyListBottomSheetRef}
          scrollListRef={viewModel.scrollListRef}
          isNearbyPharmaciesLoading={viewModel.isNearbyPharmaciesLoading}
          pharmacies={viewModel.pharmacies}
          onClickPharmacyItem={viewModel.onClickPharmacyItem}
          isIncludeDiagnosisPriceFilterSelected={viewModel.isIncludeDiagnosisPriceFilterSelected}
          isVisibleMedicinePrice={viewModel.isVisibleMedicinePrice}
          onClickCurrentLocation={viewModel.requestGeolocationPermissionInWeb}
          headerHeight={client.isMobile ? 150 : 146}
          shouldTransparent={viewModel.shouldShowPharmacyCard}
        />

        {viewModel.shouldShowPharmacyCard && (
          <PharmacyCardBottomSheet
            pharmacy={viewModel.selectedPharmacy}
            isVisibleMedicinePrice={viewModel.isVisibleMedicinePrice}
            shouldShowDiagnosisPrice={viewModel.isIncludeDiagnosisPriceFilterSelected}
            clickViewListModeButton={viewModel.onClickViewListModeButtonWithResetSelectedPharmacy}
            shouldShowPharmacyDetail
            onClickCurrentLocation={viewModel.requestGeolocationPermissionInWeb}
            pharmacyMainInfoComponent={
              <PharmacyInfoHeader
                pharmacy={viewModel.selectedPharmacy ?? viewModel.pharmacies[0]}
                onClickFavoritePharmacy={(e) => {
                  viewModel.onClickFavoritePharmacy(e, viewModel.selectedPharmacy ?? viewModel.pharmacies[0]);
                }}
              />
            }
            client={client}
          >
            <PharmacyCardBottomSheet.ButtonArea>
              <BottomWrapper>
                <ClickLogger
                  params={{
                    eventName: FindPharmacyLog.click_pharmacy_diagnosis_button,
                    payload: {
                      pharmacyId: viewModel.selectedPharmacy?.pharmacyId ?? 0,
                      price:
                        (viewModel.selectedPharmacy?.pharmacyDispensableMedicines?.totalAmount ?? 0) +
                        (viewModel.selectedPharmacy?.diagnosisPrice ?? 0),
                    },
                  }}
                >
                  <PharmacyDiagnosisActionButtonWrapper>
                    <PharmacyDiagnosisActionButton type='button' onClick={viewModel.onOpenAppInstallBottomSheet}>
                      <PharmacyDiagnosisActionButtonText>
                        {viewModel.selectedPharmacy?.getFindPharmacyDiagnosisActionButtonText(
                          viewModel.isIncludeDiagnosisPriceFilterSelected ?? false,
                          viewModel.selectedTabName,
                          viewModel.isSelectedAllTab()
                        )}
                      </PharmacyDiagnosisActionButtonText>
                    </PharmacyDiagnosisActionButton>
                    <EventTextArea>
                      <HealthMedicalMedicines />
                      <EventTextWrapper>
                        <EventText>오늘 </EventText>
                        <HighlightedEventText>1,379명</HighlightedEventText>
                        <EventText>{`이 ${treatmentProductMedicineName} 처방받았어요`}</EventText>
                      </EventTextWrapper>
                    </EventTextArea>
                  </PharmacyDiagnosisActionButtonWrapper>
                </ClickLogger>
              </BottomWrapper>
            </PharmacyCardBottomSheet.ButtonArea>
          </PharmacyCardBottomSheet>
        )}
      </TemplateLayout.Content>

      <ShareSelectModal
        isVisible={isShowShareModal}
        onClose={handleCloseShareModal}
        type={KAKAO_SHARE_TYPE.NON_BENEFIT_MAP}
        pageName={getPageName(router)}
        kakaoShare={{
          id: diagnosisTypeId ?? '',
          subId: treatmentMedicineId ?? '',
          title: `${treatmentProductMedicineName} 최저가 약국 찾기`,
          content: `내 근처에서 가장 저렴한 ${treatmentProductMedicineName} 가격을 확인하고 비교할 수 있어요.`,
          imageUrl: treatmentProductShareImageUrl,
        }}
        copyLink={{
          id: diagnosisTypeId ?? '',
          copyElementId: 'copyNonBenefitMap',
          copyLinkUrl: `${formatShareUrl(
            ROUTES.NON_BENEFIT.MAP.path
          )}?diagnosisTypeId=${diagnosisTypeId}&treatmentMedicineId=${treatmentMedicineId}&treatmentMedicineUnitId=${treatmentMedicineUnitId}&latitude=${
            viewModel.currentMapLocation.latitude
          }&longitude=${viewModel.currentMapLocation.longitude}`,
        }}
      />
      <EmptyPharmacyToast shouldShowToast={viewModel.shouldShowEmptyPharmacyMessage()} />

      {viewModel.showLoginModal && (
        <Overlay onClickOverlay={viewModel.closeLoginModal}>
          <LoginModal
            isWritePage={false}
            onClick={viewModel.closeLoginModal}
            redirectUrl=''
            trackInfo={{
              campaign: 'doctor_detail',
              content: 'web_content_btn_details',
              buttonText: '단골 의사 추가/삭제',
            }}
          />
        </Overlay>
      )}
    </TemplateLayout.Wrapper>
  );
};

export default NonBenefitMapTemplate;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const PharmacyDiagnosisActionButtonWrapper = styled.div`
  display: flex;
    flex-direction: column;
    width: 100%;
`;

const PharmacyDiagnosisActionButton = styled.button`
  width: 100%;
  display: flex;
  height: 48px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  background-color: ${theme.color.primary600};
  border-radius: 8px;
  color: white;
`;

const PharmacyDiagnosisActionButtonText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI16SB}
  `}
`;

const BottomWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
`;

const EventTextArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
    padding-top: 8px;
  gap: 8px;;
`;

const EventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15M};
    color: ${theme.color.gray500};
  `};
`;

const HighlightedEventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15SB};
    color: ${theme.color.primary700};
  `};
`;

const EventTextWrapper = styled.span``;
